<template>
  <!--
    D-44-14 — NO POLLING, PLUS PULL-TO-REFRESH. An owner decision of
    2026-09-01, not a Claude default.

    `:ptr="true"` is bound as an explicit boolean rather than as a bare
    attribute, so no empty-string cast sits between this template and the
    framework's Boolean prop.

    `PrivateGymPage.vue` declares neither `ptr` nor `ptr:refresh`, so both reach
    `f7-page` by Vue 3 attribute fallthrough — the wrapper has a single root
    element and does not set `inheritAttrs: false`. That is runtime behaviour of
    a wrapper this plan does not own, so it was MEASURED in the running app
    rather than assumed; 44-08-SUMMARY.md records the reading. The vitest suite
    stubs this wrapper, so it proves the wiring and can never prove the
    fallthrough.

    `@page:reinit` reaches `f7-page` the same way. It is the CANDIDATE hook for
    re-reading the list on a back-navigation onto a page Framework7 has cached —
    `mounted()` fires once — and `admin/news.vue` has no precedent for it. What
    it actually does here was measured, and the reading is in the SUMMARY.
    The gesture above and this hook answer DIFFERENT questions and neither may
    be deleted on the strength of the other: the gesture is the manual path and
    works today, the cached-page re-entry is the automatic one.
  -->
  <private-gym-page
    id="admin-broadcasts-page"
    name="admin-broadcasts"
    :navbar="false"
    :has_fab="true"
    :ptr="true"
    @ptr:refresh="onPullToRefresh"
    @page:reinit="fetchBroadcasts"
  >
    <!--
      The shared PageTopNavbar is deliberately switched off, exactly as on
      admin/news.vue: it carries the panel toggle and the logo, which is right
      for a member's landing surface and wrong here. This is a pushed detail
      screen and the operator needs a way back to where they came from.
    -->
    <f7-navbar title="Pushberichten" back-link="Terug"></f7-navbar>

    <div class="broadcast-column">
      <f7-block-header>
        Verstuurde pushberichten, nieuwste eerst.
      </f7-block-header>

      <!--
        FOUR MUTUALLY EXCLUSIVE STATES, driven by TWO flags and never by
        inferring state from an empty array.

        "we have not asked yet" and "we asked and there is nothing" look
        identical in `items`, and telling an operator that nothing was ever sent
        a beat before showing them the compliance record is the defect this
        v-if / v-else-if chain exists to prevent.
      -->

      <!-- 1. Loading. -->
      <f7-block v-if="loading" class="text-align-center">
        <!--
          The INLINE preloader, not the app-wide overlay: the overlay covers the
          navbar and swallows the back gesture, so page chrome and title render
          immediately behind this block instead.

          The pull gesture runs through the same load method, so it passes
          through this state too. That is deliberate: one screen, one loading
          representation, rather than a second one that only the gesture uses.
        -->
        <f7-preloader></f7-preloader>
      </f7-block>

      <!-- 2. Failure (B13). -->
      <f7-block v-else-if="error" strong inset>
        <!--
          PERSISTENT, NOT A TOAST. A load failure leaves the operator with
          nothing else to act on, so the message must not disappear on a timer.

          The screen dispatches no notification of its own: `broadcasts/fetch_all`
          is deliberately not silent, so the global handler has already shown the
          one message this failure gets. A second would be a duplicate about the
          same request.

          The server's own error text is deliberately NOT rendered (T-44-42). It
          is English, unbounded, and names nothing the operator can act on; the
          authored Dutch copy below is the whole message.
        -->
        <p class="broadcast-heading">We konden de pushberichten niet laden.</p>
        <p>Controleer je internetverbinding en probeer het opnieuw.</p>
        <f7-button fill @click="fetchBroadcasts">Opnieuw proberen</f7-button>
      </f7-block>

      <!-- 3a. Loaded, with nothing in it. -->
      <f7-block v-else-if="items.length === 0" strong inset>
        <!--
          A real empty state, because this page's only purpose is the list. The
          body names the control the operator must reach for, and that control
          is on screen while they read it.
        -->
        <p class="broadcast-heading">Nog geen pushberichten verstuurd</p>
        <p>Maak je eerste bericht met de + onderaan de pagina.</p>
      </f7-block>

      <!-- 3b. Loaded, with rows. -->
      <f7-list v-else media-list class="list-strong list-outline list-dividers broadcast-list">
        <!--
          NO `link` ON A ROW. A sent broadcast is neither editable nor
          deletable, so there is no detail route to navigate to and a row that
          looked tappable would promise one.

          The list is unbounded and unpaginated on both sides. A cap would
          silently withhold a send from the only screen that shows it, and this
          screen IS the compliance record.
        -->
        <f7-list-item
          v-for="item in items"
          :key="item.id"
          :title="item.title"
          :after="outcomeWord(item)"
          :text="rowText(item)"
        ></f7-list-item>
      </f7-list>
    </div>

    <pg-fab @click="f7router.navigate('/admin/broadcasts/new')"></pg-fab>
  </private-gym-page>
</template>

<script>
import PrivateGymPage from "../_layouts/PrivateGymPage.vue";
import PgFab from "../../components/general/PgFab.vue";

import moment from "moment/dist/moment";
import "moment/dist/locale/nl";
moment.locale("nl");

/**
 * The four delivery-outcome words, as a frozen vocabulary rather than four
 * string literals scattered through the methods below.
 *
 * A WORD AND NEVER A COLOURED MARK: a word survives greyscale, colour-vision
 * deficiency and a screen reader, and colour must never be the only signal.
 *
 * THE SERVER OWNS THE VALUE. `BroadcastResource` derives `outcome` at read time
 * and sends one of these four (44-06, 44-07), so this object does not author
 * the copy — it PINS it. `AdminBroadcastList.test.js` names the same four as
 * literals and compares, so a fifth word, a renamed word or a translation drift
 * on either side reds a test instead of reaching the record unnoticed.
 *
 * `QUEUED` is the one the client also READS rather than only renders: a row in
 * that state has no `sent_recipient_count` and no `sent_at` yet, so the row
 * text falls back to the confirmed count and the created instant.
 */
const OUTCOME = Object.freeze({
  QUEUED: "In wachtrij",
  SENT: "Verstuurd",
  PARTIAL: "Deels mislukt",
  FAILED: "Mislukt",
});

/**
 * Strict parse of a stored instant, or null.
 *
 * `sent_at` and `created_at` are ISO 8601 instants WITH an offset
 * (`2026-09-01T17:52:54+02:00`, measured by 44-07), not `Y-m-d` dates.
 * Returning null rather than an invalid moment is what keeps `Invalid date` off
 * the compliance record: every caller checks for null first.
 *
 * @param {?string} value
 * @returns {?Object} a valid moment, or null
 */
function parseInstant(value) {
  if (!value) {
    return null;
  }

  const parsed = moment(value, moment.ISO_8601, true);

  return parsed.isValid() ? parsed : null;
}

export default {
  components: { PrivateGymPage, PgFab },

  /**
   * Exposed on the component options so the test can compare the vocabulary
   * against its own four literals. It is not reactive state and no template
   * expression reads it.
   */
  OUTCOME,

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      /**
       * Starts TRUE, deliberately.
       *
       * `mounted()` runs after the first render, so a false default would paint
       * the empty state for one frame on every visit — an operator with a dozen
       * sends would see "Nog geen pushberichten verstuurd" flash before their
       * record.
       */
      loading: true,

      /**
       * Separate from `loading`, so a failed load is its own state rather than
       * an empty list. The two are never true together.
       */
      error: false,
    };
  },

  computed: {
    /**
     * The whole history, newest first.
     *
     * SORTED DEFENSIVELY, over a copy. The SERVER is the primary ordering
     * authority — `GET /v1/broadcasts` orders by id DESC through the model's own
     * `scopeNewestFirst()` (44-06/44-07), and 44-07 measured that the id
     * ordering answers differently from a `sent_at` ordering when two rows share
     * a second — so this re-sort MIRRORS that order rather than inventing one,
     * and exists only so a contract regression on the server cannot silently
     * scramble the screen.
     *
     * Over a COPY, so the store's own array keeps the order the server sent and
     * a regression stays visible in one place instead of being corrected in two.
     *
     * @returns {Array}
     */
    items() {
      return [...this.$store.getters["broadcasts/all"]].sort((left, right) => right.id - left.id);
    },
  },

  created() {
    //
  },

  mounted() {
    this.fetchBroadcasts();
  },

  methods: {
    /**
     * Loads the whole broadcast history.
     *
     * THE ONLY FETCH PATH ON THIS SCREEN. The mount, the `Opnieuw proberen`
     * button and D-44-14's pull gesture all run through here. A second path
     * would be a second definition of what the compliance record is, and being
     * the record is this screen's whole job.
     *
     * Retry running through this method is what returns the screen to its
     * preloader instead of spinning inside the failure block — one loading
     * representation per screen, not two. A repeated failure updates the same
     * block rather than stacking a second one, because there is only ever one.
     *
     * The `.catch` is required rather than optional. `broadcasts/fetch_all`
     * carries none of its own (deliberately — see the foot of
     * `store/modules/broadcasts.js`), so without a handler the rejection is
     * unhandled, which is console noise on top of the one message the operator
     * has already been shown.
     *
     * @returns {Promise} resolved once the screen has settled, either way
     */
    fetchBroadcasts() {
      this.loading = true;
      this.error = false;

      return this.$store
        .dispatch("broadcasts/fetch_all", {})
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * D-44-14's manual path: the operator's way to re-read a delivery outcome
     * that arrived after the send, without leaving the screen.
     *
     * Framework7 hands this handler a completion callback and leaves the pull
     * spinner running until it is called
     * (`framework7-vue/components/page.js:186`). It is called in a `.finally`,
     * so a FAILED re-fetch stops the spinner instead of stranding the operator
     * under one that never resolves.
     *
     * No `.catch` is added here. `fetchBroadcasts` already sets the error state
     * and `api/onError` already rendered the one message; a second handler here
     * would be a duplicate about the same request.
     *
     * The callback is type-checked before being called so this handler can also
     * be driven directly from a unit test with nothing to complete.
     *
     * @param {function} [done] the framework's completion callback
     */
    onPullToRefresh(done) {
      this.fetchBroadcasts().finally(() => {
        if (typeof done === "function") {
          done();
        }
      });
    },

    /**
     * The row's outcome word, exactly as the server derived it.
     *
     * Rendered verbatim and never re-derived. 44-06 and 44-07 measured that the
     * raw event counters can disagree with their own denominator — one job can
     * raise both a dispatch and a failure event — so the counters are
     * deliberately withheld from the resource and the derived WORD is the whole
     * reading the client is given. Re-deriving it here would be the client
     * quietly redefining a compliance column.
     *
     * @param {Object} item
     * @returns {string}
     */
    outcomeWord(item) {
      return item.outcome;
    },

    /**
     * The row's text line: `{audience} · {n} ontvanger(s) · {datum}`, with
     * `Aantal gewijzigd` between the count and the date when the send reached a
     * different number of members than the operator confirmed.
     *
     * `audience_label` is SERVER-OWNED (CD-03 / IC3) and travels with every row,
     * so this screen needs no local audience map that could drift from the
     * server's own predicate. For an `individual` send it reads `Eén lid` and
     * never the member's name: the list is a record, not a directory (T-44-40).
     *
     * @param {Object} item
     * @returns {string}
     */
    rowText(item) {
      const segments = [item.audience_label];

      const phrase = this.recipientPhrase(item);
      if (phrase !== null) {
        segments.push(phrase);
      }

      // A non-zero delta only. A ZERO delta is a real measured value — 44-07's
      // live row read `recipient_delta: 0` — and a NULL one is every row that
      // has not been sent yet. Neither is a change, and treating either as one
      // would put a warning on a record that is exactly as confirmed.
      if (typeof item.recipient_delta === "number" && item.recipient_delta !== 0) {
        segments.push("Aantal gewijzigd");
      }

      segments.push(this.dateText(item));

      return segments.join(" · ");
    },

    /**
     * The count, as a phrase with its own singular grammar.
     *
     * Falls back to the CONFIRMED count while the row is still queued:
     * `sent_recipient_count` is null until the send settles, and rendering that
     * raw would put `null ontvangers` on the compliance record.
     *
     * Returns null rather than a phrase when neither count is a number, so a
     * legacy or regressed row loses one segment instead of rendering nonsense.
     *
     * @param {Object} item
     * @returns {?string}
     */
    recipientPhrase(item) {
      const count = item.outcome === OUTCOME.QUEUED
        ? item.confirmed_recipient_count
        : item.sent_recipient_count;

      if (typeof count !== "number" || !isFinite(count)) {
        return null;
      }

      return count + (count === 1 ? " ontvanger" : " ontvangers");
    },

    /**
     * The row's date segment.
     *
     * Falls back to the CREATED instant while the row is still queued, because
     * `sent_at` is null until the send settles. Guards the format call rather
     * than formatting a null, so a legacy or regressed row renders authored
     * Dutch instead of `Invalid date`.
     *
     * @param {Object} item
     * @returns {string}
     */
    dateText(item) {
      const instant = parseInstant(item.sent_at) || parseInstant(item.created_at);

      if (instant === null) {
        return "Geen datum";
      }

      return instant.format("D MMMM YYYY HH:mm");
    },
  },

  watch: {
    //
  },
};
</script>

<style scoped>
/*
  Framework7 blocks and lists are full-bleed, and this phase must support the
  desktop-web build as well as the phone. A list spanning 1,400px reads badly.

  672px is the value admin/news.vue already ships, taken from
  --f7-card-expandable-tablet-width (670px). One reading width across the admin
  surfaces rather than two.

  Below 768px the page is full-bleed exactly as every other page in the app is.
*/
@media (min-width: 768px) {
  .broadcast-column {
    max-width: 672px;
    margin-left: auto;
    margin-right: auto;
  }
}

/*
  IC12 / M1. `--f7-list-item-title-white-space` is `nowrap` at the framework's
  own root, so a broadcast title — author-written and up to 120 characters —
  would ellipsise at 320px. It wraps instead.

  SCOPED TO THIS ONE LIST, exactly as reserveringen.scss scopes the same token.
  Setting it at :root would change every list in the app for one screen.
*/
.broadcast-list {
  --f7-list-item-title-white-space: normal;
}

/*
  The Heading role, taken entirely from Framework7's own tokens. No numeric size
  is authored here or anywhere else on this page: a fifth size on one screen is
  how a design system stops being one.
*/
.broadcast-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}
</style>
